/**
 * Cell for a list of free-text tags.
 *
 * The value is `string[]`, and every write sends the whole list, because that
 * is what the update endpoints behind it take. The popover is the editor rather
 * than the cell itself: a tag is added, removed and read one at a time, and an
 * inline input would have to encode the list as one string and guess where the
 * separators were meant.
 */
import { useState } from 'react';
import { Badge, Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { PlusIcon, TagIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TagsCustomCellProps {
  value: string[] | undefined;
  /**
   * Always the whole list, empty included. An empty list is how the update
   * endpoints behind this spell "no tags"; `null` is not a value they take.
   */
  onChange: (next: string[]) => void;
}

export function TagsCustomCell({ value, onChange }: TagsCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const tags = value ?? [];

  /**
   * Returns whether the tag was taken, so the caller can decide what to do with
   * the text still in the input. A repeat is dropped rather than refused: the
   * list is a set as far as the user is concerned, and there is nothing to
   * correct, but the text stays put so it is clear nothing was added.
   */
  const addTag = (raw: string): boolean => {
    const tag = raw.trim();
    if (!tag || tags.includes(tag)) return false;
    onChange([...tags, tag]);
    return true;
  };

  const addDraft = () => {
    if (addTag(draft)) setDraft('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Closing on typed-but-unadded text saves it rather than dropping it:
        // clicking away from a tag you just typed reads as finishing, and
        // silently discarding it is indistinguishable from a failed write.
        if (!next) {
          addTag(draft);
          setDraft('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex h-7 w-full items-center gap-1 overflow-hidden rounded px-1 text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {tags.length === 0 ? (
            <TagIcon
              className="size-4 text-muted-foreground"
              aria-label={t('trackersBoard.tagsCustomCell.addTags')}
            />
          ) : (
            tags.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                roundness="full"
                className="max-w-full shrink-0 truncate font-normal"
              >
                {tag}
              </Badge>
            ))
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap gap-1">
          {tags.length === 0 ? (
            <p className="px-1 py-0.5 text-xs text-muted-foreground">
              {t('trackersBoard.tagsCustomCell.noTagsYet')}
            </p>
          ) : (
            tags.map((tag) => (
              <Badge key={tag} variant="outline" roundness="full" className="gap-1 font-normal">
                <span className="truncate">{tag}</span>
                <button
                  type="button"
                  onClick={() => onChange(tags.filter((current) => current !== tag))}
                  aria-label={t('trackersBoard.tagsCustomCell.removeTag', { tag })}
                  className="rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <XIcon className="size-3" aria-hidden />
                </button>
              </Badge>
            ))
          )}
        </div>
        <div className="mt-2 flex items-center gap-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDraft();
              }
            }}
            placeholder={t('trackersBoard.tagsCustomCell.newTag')}
            aria-label={t('trackersBoard.tagsCustomCell.newTag')}
          />
          <button
            type="button"
            onClick={addDraft}
            disabled={draft.trim().length === 0}
            aria-label={t('trackersBoard.tagsCustomCell.addTag')}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PlusIcon className="size-4" aria-hidden />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
